import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity, Modal, FlatList, Platform } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, Button, Chip, TextInput } from 'react-native-paper';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import api from '../../services/api';

const formatDateYMD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ESTADOS_CONFIG = {
  borrador:         { label: 'Borrador',     color: '#6C757D', icon: 'create-outline' },
  confirmada:       { label: 'Confirmada',   color: '#007BFF', icon: 'checkmark-circle' },
  recibida_parcial: { label: 'Parcial',      color: '#FFC107', icon: 'hourglass' },
  recibida:         { label: 'Recibida',     color: '#28A745', icon: 'checkmark-done-circle' },
  cancelada:        { label: 'Cancelada',    color: '#DC3545', icon: 'close-circle' },
};

const DetalleCompraScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { compraId } = route.params;

  const [compra, setCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRecepcionModal, setShowRecepcionModal] = useState(false);
  const [recepcionItems, setRecepcionItems] = useState([]);
  const [fechaPickerIdx, setFechaPickerIdx] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCompra(compraId);
      setCompra(data);
    } catch (error) {
      console.error('Error al cargar compra:', error);
      Alert.alert('Error', 'No se pudo cargar la orden de compra');
    } finally {
      setLoading(false);
    }
  }, [compraId]);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const confirmarOC = () => {
    Alert.alert(
      'Confirmar Orden',
      `Confirmar la orden ${compra.numero_compra}? Una vez confirmada no se puede editar.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              setActionLoading(true);
              await api.confirmarCompra(compraId);
              Alert.alert('Orden Confirmada', 'La orden ha sido confirmada');
              loadData();
            } catch (error) {
              Alert.alert('Error', error.message || 'Error al confirmar');
            } finally {
              setActionLoading(false);
            }
          }
        }
      ]
    );
  };

  const cancelarOC = () => {
    Alert.alert(
      'Cancelar Orden',
      `Cancelar la orden ${compra.numero_compra}?`,
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Si, Cancelar',
          style: 'destructive',
          onPress: async () => {
            try {
              setActionLoading(true);
              await api.cancelarCompra(compraId);
              Alert.alert('Orden Cancelada', 'La orden ha sido cancelada');
              loadData();
            } catch (error) {
              Alert.alert('Error', error.message || 'Error al cancelar');
            } finally {
              setActionLoading(false);
            }
          }
        }
      ]
    );
  };

  const abrirRecepcion = () => {
    if (!compra || !compra.items) return;

    const itemsParaRecibir = compra.items
      .filter(item => (parseInt(item.cantidad_recibida) || 0) < (parseInt(item.cantidad) || 0))
      .map(item => ({
        detalle_id: item.id,
        producto_nombre: item.producto_nombre,
        cantidad_solicitada: parseInt(item.cantidad) || 0,
        cantidad_ya_recibida: parseInt(item.cantidad_recibida) || 0,
        cantidad_pendiente: (parseInt(item.cantidad) || 0) - (parseInt(item.cantidad_recibida) || 0),
        cantidad_recibida: ((parseInt(item.cantidad) || 0) - (parseInt(item.cantidad_recibida) || 0)).toString(),
        numero_lote: '',
        fecha_vencimiento: '',
      }));

    if (itemsParaRecibir.length === 0) {
      Alert.alert('Info', 'Todos los productos ya fueron recibidos');
      return;
    }

    setRecepcionItems(itemsParaRecibir);
    setShowRecepcionModal(true);
  };

  const actualizarRecepcion = (index, campo, valor) => {
    const newItems = [...recepcionItems];
    newItems[index][campo] = valor;
    setRecepcionItems(newItems);
  };

  const procesarRecepcion = async () => {
    // Validar cantidades
    for (const item of recepcionItems) {
      const cant = parseInt(item.cantidad_recibida) || 0;
      if (cant < 0) {
        Alert.alert('Error', `Cantidad invalida para "${item.producto_nombre}"`);
        return;
      }
      if (cant > item.cantidad_pendiente) {
        Alert.alert('Error', `No puedes recibir mas de ${item.cantidad_pendiente} unidades de "${item.producto_nombre}"`);
        return;
      }
    }

    const itemsConRecepcion = recepcionItems
      .filter(item => parseInt(item.cantidad_recibida) > 0)
      .map(item => ({
        detalle_id: item.detalle_id,
        cantidad_recibida: parseInt(item.cantidad_recibida),
        numero_lote: item.numero_lote || null,
        fecha_vencimiento: item.fecha_vencimiento || null,
      }));

    if (itemsConRecepcion.length === 0) {
      Alert.alert('Error', 'Ingresa al menos una cantidad a recibir');
      return;
    }

    try {
      setActionLoading(true);
      const result = await api.recibirCompra(compraId, { items_recibidos: itemsConRecepcion });
      setShowRecepcionModal(false);
      Alert.alert('Recepcion Registrada', result.message);
      loadData();
    } catch (error) {
      Alert.alert('Error', error.message || 'Error al registrar recepcion');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text style={styles.loadingText}>Cargando orden de compra...</Text>
      </View>
    );
  }

  if (!compra) {
    return (
      <View style={styles.loadingContainer}>
        <Ionicons name="alert-circle-outline" size={64} color="#DC3545" />
        <Text style={styles.errorText}>No se encontro la orden de compra</Text>
      </View>
    );
  }

  const config = ESTADOS_CONFIG[compra.estado] || ESTADOS_CONFIG.borrador;

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Cabecera */}
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.headerRow}>
              <Text style={styles.numero}>{compra.numero_compra}</Text>
              <Chip
                style={[styles.estadoChip, { backgroundColor: config.color }]}
                textStyle={styles.estadoChipText}
                icon={() => <Ionicons name={config.icon} size={16} color="#fff" />}
              >
                {config.label}
              </Chip>
            </View>

            <Divider style={styles.divider} />

            {/* Proveedor */}
            <View style={styles.infoSection}>
              <Text style={styles.label}>Proveedor</Text>
              <Text style={styles.value}>{compra.proveedor_nombre}</Text>
              {compra.proveedor_telefono && <Text style={styles.subValue}>{compra.proveedor_telefono}</Text>}
              {compra.proveedor_email && <Text style={styles.subValue}>{compra.proveedor_email}</Text>}
              {compra.proveedor_rtn && <Text style={styles.subValue}>RTN: {compra.proveedor_rtn}</Text>}
            </View>

            {/* Info general */}
            <View style={styles.infoGrid}>
              <View style={styles.infoItem}>
                <Text style={styles.label}>Fecha</Text>
                <Text style={styles.value}>{formatFecha(compra.fecha)}</Text>
              </View>
              <View style={styles.infoItem}>
                <Text style={styles.label}>Creado por</Text>
                <Text style={styles.value}>{compra.usuario_nombre}</Text>
              </View>
            </View>

            {compra.factura_proveedor && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Factura Proveedor</Text>
                <Text style={styles.value}>{compra.factura_proveedor}</Text>
              </View>
            )}

            {compra.fecha_entrega_esperada && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Entrega Esperada</Text>
                <Text style={styles.value}>{formatFecha(compra.fecha_entrega_esperada)}</Text>
              </View>
            )}

            {compra.notas && (
              <View style={styles.infoItem}>
                <Text style={styles.label}>Notas</Text>
                <Text style={styles.value}>{compra.notas}</Text>
              </View>
            )}

            {/* Timestamps de estado */}
            {compra.confirmada_en && (
              <Text style={styles.timestamp}>Confirmada: {formatFecha(compra.confirmada_en)}</Text>
            )}
            {compra.recibida_en && (
              <Text style={styles.timestamp}>Recibida: {formatFecha(compra.recibida_en)}</Text>
            )}
            {compra.cancelada_en && (
              <Text style={styles.timestamp}>Cancelada: {formatFecha(compra.cancelada_en)}</Text>
            )}
          </Card.Content>
        </Card>

        {/* Productos */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="cube" size={18} color="#007BFF" /> Productos ({compra.items?.length || 0})
            </Text>

            {(compra.items || []).map((item, index) => {
              const recibido = parseInt(item.cantidad_recibida) || 0;
              const solicitado = parseInt(item.cantidad) || 0;
              const completo = recibido >= solicitado;

              return (
                <View key={index} style={styles.productRow}>
                  <View style={styles.productInfo}>
                    <Text style={styles.productName}>{item.producto_nombre}</Text>
                    <Text style={styles.productCode}>
                      {item.producto_codigo || ''} {item.marca ? `| ${item.marca}` : ''} | {item.unidad_nombre || 'Unidad'}
                    </Text>
                    <View style={styles.productQuantities}>
                      <Text style={styles.productQty}>Solicitado: {solicitado}</Text>
                      <Text style={[styles.productQty, { color: completo ? '#28A745' : '#FFC107' }]}>
                        Recibido: {recibido}/{solicitado}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.productRight}>
                    <Text style={styles.productPrice}>L. {parseFloat(item.precio_unitario).toFixed(2)}</Text>
                    <Text style={styles.productSubtotal}>L. {parseFloat(item.subtotal).toFixed(2)}</Text>
                    {completo && <Ionicons name="checkmark-circle" size={18} color="#28A745" />}
                  </View>
                </View>
              );
            })}

            <Divider style={styles.divider} />

            {/* Totales */}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal:</Text>
              <Text style={styles.totalValue}>L. {parseFloat(compra.subtotal).toFixed(2)}</Text>
            </View>
            {parseFloat(compra.impuesto) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Impuesto:</Text>
                <Text style={styles.totalValue}>L. {parseFloat(compra.impuesto).toFixed(2)}</Text>
              </View>
            )}
            {parseFloat(compra.descuento) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Descuento:</Text>
                <Text style={[styles.totalValue, { color: '#DC3545' }]}>-L. {parseFloat(compra.descuento).toFixed(2)}</Text>
              </View>
            )}
            <View style={[styles.totalRow, styles.grandTotal]}>
              <Text style={styles.grandTotalLabel}>TOTAL:</Text>
              <Text style={styles.grandTotalValue}>L. {parseFloat(compra.total).toFixed(2)}</Text>
            </View>
          </Card.Content>
        </Card>

        {/* Acciones */}
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>
              <Ionicons name="flash" size={18} color="#007BFF" /> Acciones
            </Text>

            {compra.estado === 'borrador' && (
              <>
                <Button
                  mode="contained"
                  onPress={confirmarOC}
                  loading={actionLoading}
                  disabled={actionLoading}
                  style={[styles.actionButton, { backgroundColor: '#007BFF' }]}
                  icon="check"
                >
                  Confirmar Orden
                </Button>
                <Button
                  mode="outlined"
                  onPress={cancelarOC}
                  disabled={actionLoading}
                  style={styles.actionButton}
                  textColor="#DC3545"
                  icon="close"
                >
                  Cancelar Orden
                </Button>
              </>
            )}

            {(compra.estado === 'confirmada' || compra.estado === 'recibida_parcial') && (
              <>
                <Button
                  mode="contained"
                  onPress={abrirRecepcion}
                  loading={actionLoading}
                  disabled={actionLoading}
                  style={[styles.actionButton, { backgroundColor: '#28A745' }]}
                  icon="package-down"
                >
                  Recibir Mercaderia
                </Button>
                {compra.estado === 'confirmada' && (
                  <Button
                    mode="outlined"
                    onPress={cancelarOC}
                    disabled={actionLoading}
                    style={styles.actionButton}
                    textColor="#DC3545"
                    icon="close"
                  >
                    Cancelar Orden
                  </Button>
                )}
              </>
            )}

            {(compra.estado === 'recibida' || compra.estado === 'cancelada') && (
              <View style={styles.noActions}>
                <Ionicons name="information-circle-outline" size={24} color="#888" />
                <Text style={styles.noActionsText}>
                  Esta orden esta {compra.estado === 'recibida' ? 'completamente recibida' : 'cancelada'}
                </Text>
              </View>
            )}
          </Card.Content>
        </Card>
      </ScrollView>

      {/* Modal Recepcion */}
      <Modal visible={showRecepcionModal} animationType="slide" onRequestClose={() => setShowRecepcionModal(false)}>
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Recibir Mercaderia</Text>
            <TouchableOpacity onPress={() => setShowRecepcionModal(false)}>
              <Ionicons name="close" size={28} color="#333" />
            </TouchableOpacity>
          </View>

          <FlatList
            data={recepcionItems}
            keyExtractor={(item, index) => index.toString()}
            contentContainerStyle={{ padding: 12 }}
            renderItem={({ item, index }) => (
              <Card style={styles.recepcionCard}>
                <Card.Content>
                  <Text style={styles.recepcionProducto}>{item.producto_nombre}</Text>
                  <Text style={styles.recepcionInfo}>
                    Solicitado: {item.cantidad_solicitada} | Ya recibido: {item.cantidad_ya_recibida} | Pendiente: {item.cantidad_pendiente}
                  </Text>

                  <TextInput
                    label="Cantidad a recibir"
                    value={item.cantidad_recibida}
                    onChangeText={(v) => actualizarRecepcion(index, 'cantidad_recibida', v)}
                    mode="outlined"
                    keyboardType="numeric"
                    style={styles.recepcionInput}
                    dense
                  />

                  <TextInput
                    label="No. de Lote (opcional)"
                    value={item.numero_lote}
                    onChangeText={(v) => actualizarRecepcion(index, 'numero_lote', v)}
                    mode="outlined"
                    style={styles.recepcionInput}
                    dense
                  />

                  <Text style={styles.fechaLabel}>Fecha de Vencimiento</Text>
                  <TouchableOpacity
                    style={styles.fechaBtn}
                    onPress={() => setFechaPickerIdx(index)}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="calendar-outline" size={18} color="#0066cc" />
                    <Text style={[styles.fechaBtnText, !item.fecha_vencimiento && styles.fechaBtnPlaceholder]}>
                      {item.fecha_vencimiento || 'Seleccionar fecha (opcional)'}
                    </Text>
                    {item.fecha_vencimiento ? (
                      <TouchableOpacity
                        onPress={() => actualizarRecepcion(index, 'fecha_vencimiento', '')}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="close-circle" size={18} color="#DC3545" />
                      </TouchableOpacity>
                    ) : (
                      <Ionicons name="chevron-down" size={16} color="#888" />
                    )}
                  </TouchableOpacity>
                </Card.Content>
              </Card>
            )}
          />
          {fechaPickerIdx !== null && (
            <DateTimePicker
              value={
                recepcionItems[fechaPickerIdx]?.fecha_vencimiento
                  ? new Date(recepcionItems[fechaPickerIdx].fecha_vencimiento)
                  : new Date()
              }
              mode="date"
              display={Platform.OS === 'ios' ? 'inline' : 'default'}
              minimumDate={new Date()}
              onChange={(event, selectedDate) => {
                const idx = fechaPickerIdx;
                setFechaPickerIdx(Platform.OS === 'ios' ? idx : null);
                if (selectedDate) {
                  actualizarRecepcion(idx, 'fecha_vencimiento', formatDateYMD(selectedDate));
                  if (Platform.OS !== 'ios') setFechaPickerIdx(null);
                }
              }}
            />
          )}

          <View style={styles.modalFooter}>
            <Button
              mode="contained"
              onPress={procesarRecepcion}
              loading={actionLoading}
              disabled={actionLoading}
              style={{ backgroundColor: '#28A745' }}
              icon="check"
            >
              Confirmar Recepcion
            </Button>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  errorText: { marginTop: 10, color: '#DC3545', fontSize: 16 },
  scrollContent: { padding: 8, paddingBottom: 30 },
  card: { marginBottom: 10, elevation: 2 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  numero: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  estadoChip: { height: 28 },
  estadoChipText: { fontSize: 12, color: '#fff', fontWeight: 'bold' },
  divider: { marginVertical: 10 },
  infoSection: { marginBottom: 10 },
  infoGrid: { flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' },
  infoItem: { marginBottom: 8, minWidth: '45%' },
  label: { fontSize: 12, color: '#888', fontWeight: '600', textTransform: 'uppercase' },
  value: { fontSize: 14, color: '#333', marginTop: 2 },
  subValue: { fontSize: 13, color: '#666' },
  timestamp: { fontSize: 12, color: '#888', marginTop: 4 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 10 },
  productRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F0F0F0' },
  productInfo: { flex: 1, marginRight: 10 },
  productName: { fontSize: 14, fontWeight: 'bold', color: '#333' },
  productCode: { fontSize: 12, color: '#888', marginTop: 2 },
  productQuantities: { flexDirection: 'row', marginTop: 4, gap: 12 },
  productQty: { fontSize: 12, color: '#666' },
  productRight: { alignItems: 'flex-end' },
  productPrice: { fontSize: 12, color: '#888' },
  productSubtotal: { fontSize: 14, fontWeight: 'bold', color: '#333', marginTop: 2 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  totalLabel: { fontSize: 14, color: '#666' },
  totalValue: { fontSize: 14, fontWeight: '600', color: '#333' },
  grandTotal: { marginTop: 4, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#DEE2E6' },
  grandTotalLabel: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  grandTotalValue: { fontSize: 18, fontWeight: 'bold', color: '#28A745' },
  actionButton: { marginBottom: 8 },
  noActions: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },
  noActionsText: { marginLeft: 8, color: '#888', fontSize: 14 },
  // Modal Recepcion
  modalContainer: { flex: 1, backgroundColor: '#f5f5f5' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#EEE' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  modalFooter: { padding: 16, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#EEE' },
  recepcionCard: { marginBottom: 10, elevation: 1 },
  recepcionProducto: { fontSize: 15, fontWeight: 'bold', color: '#333', marginBottom: 4 },
  recepcionInfo: { fontSize: 13, color: '#666', marginBottom: 8 },
  recepcionInput: { marginBottom: 6, backgroundColor: '#fff' },
  fechaLabel: { fontSize: 12, color: '#666', marginBottom: 4, marginTop: 4 },
  fechaBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1, borderColor: '#ccc', borderRadius: 4,
    backgroundColor: '#fff', paddingHorizontal: 10, paddingVertical: 10,
    marginBottom: 6,
  },
  fechaBtnText: { flex: 1, fontSize: 14, color: '#333' },
  fechaBtnPlaceholder: { color: '#999' },
});

export default DetalleCompraScreen;
